import { useState } from 'react';
import { useNewlyVerified } from '@/hooks/useNewlyVerified';
import { BellIcon } from '@/components/nav/navIcons';

const AUDIENCE_LABEL: Record<string, string> = { women: 'Women', men: 'Men', child: 'Youth' };
const CATEGORY_LABEL: Record<string, string> = { tops: 'Tops', bottoms: 'Bottoms' };

/**
 * Real notifications only — the badge count is however many brand/audience/
 * category combinations were verified since this device last checked (see
 * useNewlyVerified). Never a placeholder or invented number.
 */
export function NotificationBell() {
  const { newItems, markSeen } = useNewlyVerified();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={newItems.length > 0 ? `Notifications, ${newItems.length} new` : 'Notifications'}
        className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-ink hover:border-brand/40"
      >
        <BellIcon className="h-5 w-5" />
        {newItems.length > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-ink">
            {newItems.length}
          </span>
        )}
      </button>

      {open && (
        <div
          role="region"
          aria-label="Notifications"
          className="absolute right-0 z-20 mt-2 w-72 rounded-2xl border border-stone-200 bg-white p-3 shadow-card"
        >
          <p className="text-sm font-semibold text-ink">Newly verified brands</p>
          {newItems.length === 0 ? (
            <p className="mt-2 text-sm text-muted">Nothing new since your last visit.</p>
          ) : (
            <>
              <ul className="mt-2 max-h-64 space-y-1.5 overflow-y-auto">
                {newItems.map((item) => (
                  <li key={`${item.brandSlug}-${item.audience}-${item.category}`} className="text-sm text-ink">
                    <span className="font-medium">{item.brandName}</span>{' '}
                    <span className="text-muted">
                      — {AUDIENCE_LABEL[item.audience]} · {CATEGORY_LABEL[item.category]}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  markSeen();
                  setOpen(false);
                }}
                className="mt-3 min-h-[36px] w-full rounded-xl border border-stone-200 text-xs font-semibold text-ink hover:border-brand/40"
              >
                Mark all as read
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
